"use client";

/**
 * useWhiteboard – live-session side of the tutor's interactive whiteboard.
 *
 * Listens for `whiteboard_show` / `whiteboard_clear` messages from the voice
 * agent (topic "whiteboard") and publishes learner interactions back on the
 * same topic so the tutor can react by voice. `whiteboard_update` applies
 * visible board mutations such as annotations, highlights, drawings, hints,
 * option changes, and locked word tiles. Protocol: lib/whiteboard/spec.ts and
 * lib/whiteboard_runtime.py. Resolved boards auto-hide locally after a brief
 * success pause and publish an `auto_hidden` lifecycle event so the tutor
 * runtime does not keep referring to a board the learner no longer sees.
 */
import type { WhiteboardCardEvent } from "@/components/whiteboard/types";
import { createLogger } from "@/lib/logger";
import { recordWebPractice } from "@/lib/practice-client";
import {
  MAX_WHITEBOARD_BOARD_RESULTS,
  type WhiteboardBoardResult,
  type WhiteboardSessionBoard,
  whiteboardKeySentence,
} from "@/lib/whiteboard/boardResults";
import {
  type WhiteboardRenderState,
  applyWhiteboardUpdates,
  resetWhiteboardRenderState,
} from "@/lib/whiteboard/boardState";
import { controllerKey, controllers } from "@/lib/whiteboard/generated/controller";
import { connectGeneratedRoom } from "@/lib/whiteboard/generated/transport";
import { setLastSessionBoards } from "@/lib/whiteboard/sessionBoardStore";
import {
  WHITEBOARD_TOPIC,
  WhiteboardAgentMessageSchema,
  type WhiteboardInteractionEvent,
  type WhiteboardSpec,
  normalizeAnswer,
} from "@/lib/whiteboard/spec";
import { type Room, RoomEvent } from "livekit-client";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useRef, useState } from "react";

const logger = createLogger("useWhiteboard");
const WHITEBOARD_WORKING_EVENT_IDLE_MS = 3500;
/** Highlights are a pointing gesture: they pulse briefly, then fade. */
const WHITEBOARD_HIGHLIGHT_PULSE_MS = 4000;
const MAX_SEEN_WHITEBOARD_SHOW_IDS = 100;

interface WhiteboardClientState {
  spec: WhiteboardSpec | null;
  renderState: WhiteboardRenderState;
  minimizedByAgent: boolean;
  /** Increments only when the tutor shows or updates visible board content. */
  tutorRevision: number;
}

export type { WhiteboardSessionBoard };

interface ActiveBoardTracking {
  specId: string;
  attempts: number;
  correct: number;
  incorrect: number;
  resolved: boolean;
  /** True once the learner has touched the board (commit or idle working). */
  touched: boolean;
  /** True once this board has been appended to the session history. */
  recorded: boolean;
}

// Session recaps retain readable material, never an executable session reference.
function recapSpec(spec: WhiteboardSpec): WhiteboardSpec {
  if (spec.kind !== "composed" || !spec.generated) return spec;
  const copy = { ...spec };
  delete copy.generated;
  return copy;
}

function buildBoardResult(
  boardSpec: WhiteboardSpec,
  tracking: ActiveBoardTracking,
  status: WhiteboardBoardResult["status"]
): WhiteboardBoardResult {
  return {
    exerciseId: boardSpec.id,
    kind: boardSpec.kind,
    label: whiteboardKeySentence(boardSpec),
    status,
    attempts: tracking.attempts,
    correct: tracking.correct,
    incorrect: tracking.incorrect,
  };
}

function emptyClientState(tutorRevision = 0): WhiteboardClientState {
  return {
    spec: null,
    renderState: resetWhiteboardRenderState(),
    minimizedByAgent: false,
    tutorRevision,
  };
}

function buildWhiteboardInteractionEvent(
  spec: WhiteboardSpec,
  cardEvent: WhiteboardCardEvent,
  interactionCount = 1
): WhiteboardInteractionEvent {
  const progress =
    cardEvent.event === "working" && interactionCount > 1
      ? [cardEvent.progress, `${interactionCount} recent board actions`].filter(Boolean).join("; ")
      : cardEvent.progress;

  return {
    type: "whiteboard_event",
    exerciseId: spec.id,
    kind: spec.kind,
    event: cardEvent.event,
    detail: {
      response: cardEvent.response,
      correct: cardEvent.correct,
      action: cardEvent.action,
      target: cardEvent.target,
      expected: cardEvent.expected,
      progress,
      resolved: cardEvent.resolved,
      attempt: cardEvent.attempt,
      block: cardEvent.block,
      targetId: cardEvent.targetId,
    },
  };
}

export function useWhiteboard(room: Room | undefined) {
  const posthog = usePostHog();
  const [board, setBoard] = useState<WhiteboardClientState>(emptyClientState);
  const { spec, renderState } = board;
  const [boardHistory, setBoardHistory] = useState<WhiteboardSessionBoard[]>([]);
  const autoHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const highlightFadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWorkingEventRef = useRef<WhiteboardInteractionEvent | null>(null);
  const pendingWorkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingWorkingCountRef = useRef(0);
  const specRef = useRef<WhiteboardSpec | null>(null);
  const trackingRef = useRef<ActiveBoardTracking | null>(null);
  const speechAttemptsRef = useRef<Map<string, number>>(new Map());
  const observedSpeechRef = useRef<Set<string>>(new Set());
  const seenShowIdsRef = useRef<Set<string>>(new Set());
  const posthogRef = useRef(posthog);

  useEffect(() => {
    posthogRef.current = posthog;
  }, [posthog]);

  const captureWhiteboardAnalytics = useCallback(
    (
      eventName: "whiteboard_shown" | "whiteboard_result_recorded",
      properties: Record<string, string | number>
    ) => {
      const client = posthogRef.current;
      if (!client || !process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
      try {
        client.capture(eventName, properties);
      } catch (error) {
        logger.warn("Failed to capture whiteboard analytics", {
          eventName,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    },
    []
  );

  const captureBoardResult = useCallback(
    (result: WhiteboardBoardResult) => {
      captureWhiteboardAnalytics("whiteboard_result_recorded", {
        exercise_kind: result.kind,
        result_status: result.status,
        attempt_count: result.attempts,
        correct_count: result.correct,
        incorrect_count: result.incorrect,
      });
    },
    [captureWhiteboardAnalytics]
  );

  useEffect(() => {
    specRef.current = spec;
  }, [spec]);

  // Mirror the history into the module-level store: the app unmounts
  // TranscriptionView (and this hook) as soon as a session ends, so the
  // end-of-session recap reads the boards from the store instead.
  const boardHistoryRef = useRef<WhiteboardSessionBoard[]>(boardHistory);
  useEffect(() => {
    boardHistoryRef.current = boardHistory;
    if (boardHistory.length > 0) {
      setLastSessionBoards(boardHistory);
    }
  }, [boardHistory]);

  // A new live session clears the previous recap. Parent calls this when
  // recording starts; we do not clear here on room mount because the hook
  // can remount mid-session without invalidating an in-progress recap.
  useEffect(() => {
    if (!room) return;
    seenShowIdsRef.current.clear();
    if (boardHistoryRef.current.length > 0) {
      setLastSessionBoards(boardHistoryRef.current);
    }
  }, [room]);

  const boardStatusFromTracking = useCallback(
    (
      tracking: ActiveBoardTracking,
      reason: "replaced" | "cleared" | "dismissed" | "unmount"
    ): WhiteboardBoardResult["status"] => {
      if (tracking.resolved) return "resolved";
      if (reason === "dismissed") return "dismissed";
      if (tracking.attempts > 0 || tracking.touched) return "attempted";
      return "shown";
    },
    []
  );

  const persistSessionBoards = useCallback((boards: WhiteboardSessionBoard[]) => {
    if (boards.length > 0) {
      setLastSessionBoards(boards);
    }
  }, []);

  // On unmount, flush a still-active board into the store so a session that
  // ends mid-exercise doesn't lose that board's result.
  useEffect(() => {
    return () => {
      const boardSpec = specRef.current;
      const tracking = trackingRef.current;
      let boards = boardHistoryRef.current;

      if (boardSpec && tracking && tracking.specId === boardSpec.id && !tracking.recorded) {
        tracking.recorded = true;
        const result = buildBoardResult(
          boardSpec,
          tracking,
          boardStatusFromTracking(tracking, "unmount")
        );
        captureBoardResult(result);
        boards = [...boards, { spec: recapSpec(boardSpec), result }].slice(
          -MAX_WHITEBOARD_BOARD_RESULTS
        );
      }

      persistSessionBoards(boards);
    };
  }, [boardStatusFromTracking, captureBoardResult, persistSessionBoards]);

  /** Append the given board to the session history exactly once. */
  const recordBoardResult = useCallback(
    (boardSpec: WhiteboardSpec, status: WhiteboardBoardResult["status"]) => {
      const tracking = trackingRef.current;
      if (!tracking || tracking.specId !== boardSpec.id || tracking.recorded) return;
      tracking.recorded = true;
      const result = buildBoardResult(boardSpec, tracking, status);
      captureBoardResult(result);
      setBoardHistory((current) =>
        [...current, { spec: recapSpec(boardSpec), result }].slice(-MAX_WHITEBOARD_BOARD_RESULTS)
      );
    },
    [captureBoardResult]
  );

  /** Close out the currently tracked board when it leaves the screen. */
  const finalizeActiveBoard = useCallback(
    (reason: "replaced" | "cleared" | "dismissed") => {
      const boardSpec = specRef.current;
      const tracking = trackingRef.current;
      if (!boardSpec || !tracking || tracking.specId !== boardSpec.id) return;
      recordBoardResult(boardSpec, boardStatusFromTracking(tracking, reason));
    },
    [boardStatusFromTracking, recordBoardResult]
  );

  const markBoardTouched = useCallback(() => {
    const boardSpec = specRef.current;
    if (!boardSpec) return;
    let tracking = trackingRef.current;
    if (!tracking || tracking.specId !== boardSpec.id) {
      tracking = {
        specId: boardSpec.id,
        attempts: 0,
        correct: 0,
        incorrect: 0,
        resolved: false,
        touched: true,
        recorded: false,
      };
      trackingRef.current = tracking;
      return;
    }
    tracking.touched = true;
  }, []);

  const applyCommittedOutcome = useCallback(
    (outcome: { correct?: boolean; resolved?: boolean; countsAsAttempt: boolean }) => {
      const boardSpec = specRef.current;
      if (!boardSpec) return;
      let tracking = trackingRef.current;
      if (!tracking || tracking.specId !== boardSpec.id) {
        tracking = {
          specId: boardSpec.id,
          attempts: 0,
          correct: 0,
          incorrect: 0,
          resolved: false,
          touched: false,
          recorded: false,
        };
        trackingRef.current = tracking;
      }
      tracking.touched = true;
      if (outcome.countsAsAttempt) {
        tracking.attempts += 1;
        if (outcome.correct === true) tracking.correct += 1;
        if (outcome.correct === false) tracking.incorrect += 1;
      }
      if (outcome.resolved === true && !tracking.resolved) {
        tracking.resolved = true;
        // Resolution is terminal, so the board joins the recap gallery now
        // rather than waiting for the replace/clear that may never come.
        recordBoardResult(boardSpec, "resolved");
      }
    },
    [recordBoardResult]
  );

  const cancelAutoHide = useCallback(() => {
    if (autoHideTimerRef.current) {
      clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = null;
    }
  }, []);

  const cancelHighlightFade = useCallback(() => {
    if (highlightFadeTimerRef.current) {
      clearTimeout(highlightFadeTimerRef.current);
      highlightFadeTimerRef.current = null;
    }
  }, []);

  const scheduleHighlightFade = useCallback(() => {
    cancelHighlightFade();
    highlightFadeTimerRef.current = setTimeout(() => {
      highlightFadeTimerRef.current = null;
      setBoard((current) =>
        current.renderState.highlightTargets.length > 0
          ? {
              ...current,
              renderState: { ...current.renderState, highlightTargets: [] },
            }
          : current
      );
    }, WHITEBOARD_HIGHLIGHT_PULSE_MS);
  }, [cancelHighlightFade]);

  const clearPendingWorkingEvent = useCallback(() => {
    if (pendingWorkingTimerRef.current) {
      clearTimeout(pendingWorkingTimerRef.current);
      pendingWorkingTimerRef.current = null;
    }
    pendingWorkingEventRef.current = null;
    pendingWorkingCountRef.current = 0;
  }, []);

  useEffect(() => cancelAutoHide, [cancelAutoHide]);
  useEffect(() => cancelHighlightFade, [cancelHighlightFade]);
  useEffect(() => clearPendingWorkingEvent, [clearPendingWorkingEvent]);

  useEffect(() => {
    if (!room) return;

    const handleData = (
      payload: Uint8Array,
      _participant?: unknown,
      _kind?: unknown,
      topic?: string
    ) => {
      if (topic !== WHITEBOARD_TOPIC) return;
      try {
        const raw = JSON.parse(new TextDecoder().decode(payload));
        const parsed = WhiteboardAgentMessageSchema.safeParse(raw);
        if (!parsed.success) {
          // Interaction events echo on the same topic; ignore anything that
          // isn't an agent -> client message.
          return;
        }
        if (parsed.data.type === "whiteboard_show") {
          const nextSpec = parsed.data.spec;
          if (seenShowIdsRef.current.has(nextSpec.id)) {
            logger.info("Ignored duplicate whiteboard show", { id: nextSpec.id });
            return;
          }
          seenShowIdsRef.current.add(nextSpec.id);
          while (seenShowIdsRef.current.size > MAX_SEEN_WHITEBOARD_SHOW_IDS) {
            const oldest = seenShowIdsRef.current.values().next().value as string | undefined;
            if (!oldest) break;
            seenShowIdsRef.current.delete(oldest);
          }
          cancelAutoHide();
          cancelHighlightFade();
          clearPendingWorkingEvent();
          observedSpeechRef.current.clear();
          finalizeActiveBoard("replaced");
          trackingRef.current =
            nextSpec.kind === "reading_story"
              ? null
              : {
                  specId: nextSpec.id,
                  attempts: 0,
                  correct: 0,
                  incorrect: 0,
                  resolved: false,
                  touched: false,
                  recorded: false,
                };
          logger.info("Whiteboard exercise received", {
            kind: parsed.data.spec.kind,
            id: parsed.data.spec.id,
          });
          captureWhiteboardAnalytics("whiteboard_shown", {
            exercise_kind: parsed.data.spec.kind,
          });
          setBoard((current) => ({
            spec: nextSpec,
            renderState: resetWhiteboardRenderState(),
            minimizedByAgent: false,
            tutorRevision: current.tutorRevision + 1,
          }));
        } else if (parsed.data.type === "whiteboard_clear") {
          if (parsed.data.exerciseId && specRef.current?.id !== parsed.data.exerciseId) {
            logger.info("Ignored stale whiteboard clear", {
              exerciseId: parsed.data.exerciseId,
              currentExerciseId: specRef.current?.id,
            });
            return;
          }
          cancelAutoHide();
          cancelHighlightFade();
          clearPendingWorkingEvent();
          observedSpeechRef.current.clear();
          finalizeActiveBoard("cleared");
          setBoard((current) =>
            current.spec
              ? {
                  ...current,
                  minimizedByAgent: true,
                }
              : emptyClientState(current.tutorRevision)
          );
        } else {
          if (parsed.data.exerciseId && specRef.current?.id !== parsed.data.exerciseId) {
            logger.info("Ignored stale whiteboard update", {
              exerciseId: parsed.data.exerciseId,
              currentExerciseId: specRef.current?.id,
            });
            return;
          }
          const ops = parsed.data.ops;
          setBoard((current) => {
            if (!current.spec) return current;
            const next = applyWhiteboardUpdates(current.spec, current.renderState, ops);
            return {
              spec: next.spec,
              renderState: next.renderState,
              minimizedByAgent: false,
              tutorRevision: current.tutorRevision + 1,
            };
          });
          // Each update batch resets highlights, so a batch with highlight
          // ops is exactly a batch that leaves highlights on screen.
          if (ops.some((op) => op.op === "highlight")) {
            scheduleHighlightFade();
          } else {
            cancelHighlightFade();
          }
          logger.info("Whiteboard update applied", {
            opCount: ops.length,
          });
        }
      } catch {
        // Not JSON / not for us.
      }
    };

    const disconnectGenerated =
      typeof room.registerByteStreamHandler === "function"
        ? connectGeneratedRoom(
            room,
            (nextSpec) => {
              cancelAutoHide();
              clearPendingWorkingEvent();
              if (specRef.current?.id !== nextSpec.id) {
                finalizeActiveBoard("replaced");
                trackingRef.current = {
                  specId: nextSpec.id,
                  attempts: 0,
                  correct: 0,
                  incorrect: 0,
                  resolved: false,
                  touched: false,
                  recorded: false,
                };
                captureWhiteboardAnalytics("whiteboard_shown", { exercise_kind: "generated" });
              } else if (
                nextSpec.kind === "composed" &&
                nextSpec.generated &&
                trackingRef.current
              ) {
                const snapshot = controllers.get(controllerKey(nextSpec.generated))?.snapshot;
                trackingRef.current.resolved =
                  !!snapshot &&
                  snapshot.artifact.program.activities.every(
                    (a) => snapshot.state.results[a.id] === "correct"
                  );
                // A revision can add practice after completion. Replace its recap when finalized.
                trackingRef.current.recorded = false;
                setBoardHistory((boards) =>
                  boards.filter((entry) => entry.spec.id !== nextSpec.id)
                );
              }
              specRef.current = nextSpec;
              setBoard((current) => ({
                spec: nextSpec,
                renderState: resetWhiteboardRenderState(),
                minimizedByAgent: false,
                tutorRevision: current.tutorRevision + 1,
              }));
            },
            (event) => {
              if (event.id !== specRef.current?.id) return;
              if (event.type === "hidden" && event.reason === "return_to_conversation") {
                setBoard((current) => ({ ...current, minimizedByAgent: true }));
              }
              if (event.type === "check")
                applyCommittedOutcome({
                  correct: event.result === "pending" ? undefined : event.result === "correct",
                  resolved: event.resolved === true,
                  countsAsAttempt: true,
                });
            }
          )
        : undefined;
    room.on(RoomEvent.DataReceived, handleData);
    return () => {
      disconnectGenerated?.();
      room.off(RoomEvent.DataReceived, handleData);
    };
  }, [
    cancelAutoHide,
    cancelHighlightFade,
    captureWhiteboardAnalytics,
    clearPendingWorkingEvent,
    finalizeActiveBoard,
    room,
    scheduleHighlightFade,
    applyCommittedOutcome,
  ]);

  const publishEvent = useCallback(
    async (event: WhiteboardInteractionEvent) => {
      if (!room) return;
      try {
        await room.localParticipant.publishData(new TextEncoder().encode(JSON.stringify(event)), {
          reliable: true,
          topic: WHITEBOARD_TOPIC,
        });
      } catch (err) {
        logger.error("Failed to publish whiteboard event", {
          error: err instanceof Error ? err.message : String(err),
        });
      }
    },
    [room]
  );

  const scheduleWorkingEvent = useCallback(
    (event: WhiteboardInteractionEvent) => {
      if (pendingWorkingTimerRef.current) {
        clearTimeout(pendingWorkingTimerRef.current);
      }
      pendingWorkingEventRef.current = event;
      pendingWorkingTimerRef.current = setTimeout(() => {
        const pending = pendingWorkingEventRef.current;
        pendingWorkingTimerRef.current = null;
        pendingWorkingEventRef.current = null;
        pendingWorkingCountRef.current = 0;
        if (pending) {
          markBoardTouched();
          void publishEvent(pending);
        }
      }, WHITEBOARD_WORKING_EVENT_IDLE_MS);
    },
    [markBoardTouched, publishEvent]
  );

  const scheduleAutoHide = useCallback(
    (resolvedSpecId: string, resolvedKind: WhiteboardSpec["kind"]) => {
      cancelAutoHide();
      autoHideTimerRef.current = setTimeout(() => {
        autoHideTimerRef.current = null;
        if (specRef.current?.id !== resolvedSpecId) return;
        void publishEvent({
          type: "whiteboard_event",
          exerciseId: resolvedSpecId,
          kind: resolvedKind,
          event: "auto_hidden",
        });
        setBoard((current) =>
          current.spec?.id === resolvedSpecId
            ? {
                ...current,
                minimizedByAgent: true,
              }
            : current
        );
      }, 1400);
    },
    [cancelAutoHide, publishEvent]
  );

  const sendCardEvent = useCallback(
    (cardEvent: WhiteboardCardEvent) => {
      if (!spec) return;
      if (cardEvent.event === "answered" || cardEvent.event === "completed") {
        void recordWebPractice().catch(() => {});
      }
      if (spec.kind === "reading_story" && cardEvent.event === "reading_complete") {
        clearPendingWorkingEvent();
        void publishEvent(buildWhiteboardInteractionEvent(spec, cardEvent));
        return;
      }
      if (cardEvent.event === "working") {
        pendingWorkingCountRef.current += 1;
        scheduleWorkingEvent(
          buildWhiteboardInteractionEvent(spec, cardEvent, pendingWorkingCountRef.current)
        );
        return;
      }

      clearPendingWorkingEvent();
      applyCommittedOutcome({
        correct: cardEvent.correct,
        resolved: cardEvent.resolved === true || cardEvent.event === "completed",
        countsAsAttempt: cardEvent.event === "answered" || cardEvent.event === "completed",
      });
      void publishEvent(buildWhiteboardInteractionEvent(spec, cardEvent));
      if (
        spec.kind !== "equation_balance" &&
        (cardEvent.resolved === true || cardEvent.event === "completed")
      ) {
        scheduleAutoHide(spec.id, spec.kind);
      }
    },
    [
      applyCommittedOutcome,
      clearPendingWorkingEvent,
      scheduleAutoHide,
      scheduleWorkingEvent,
      spec,
      publishEvent,
    ]
  );

  const observeUserSpeech = useCallback(
    (text: string, speechId?: string) => {
      const activeSpec = specRef.current;
      const spoken = text.trim();
      if (!room || !activeSpec || activeSpec.kind !== "composed" || !spoken) return;

      const speechKey = `${activeSpec.id}:${speechId || normalizeAnswer(spoken)}`;
      if (observedSpeechRef.current.has(speechKey)) return;

      const speechTarget = findSpeechAwareTarget(activeSpec, spoken);
      if (!speechTarget) return;

      observedSpeechRef.current.add(speechKey);
      clearPendingWorkingEvent();
      const attempt = (speechAttemptsRef.current.get(activeSpec.id) ?? 0) + 1;
      speechAttemptsRef.current.set(activeSpec.id, attempt);
      applyCommittedOutcome({
        correct: undefined,
        resolved: speechTarget.matched,
        countsAsAttempt: true,
      });

      void publishEvent({
        type: "whiteboard_event",
        exerciseId: activeSpec.id,
        kind: activeSpec.kind,
        event: "speech_attempt",
        detail: {
          response: spoken,
          action: speechTarget.action,
          target: speechTarget.target,
          targetId: speechTarget.targetId,
          expected: speechTarget.expected,
          progress: speechTarget.matched
            ? "Learner practiced the visible target phrase"
            : "Learner speech was observed; transcript mismatch is not a correctness judgment",
          resolved: speechTarget.matched,
          attempt,
          block: speechTarget.block,
        },
      });
      if (speechTarget.matched) {
        scheduleAutoHide(activeSpec.id, activeSpec.kind);
      }
    },
    [applyCommittedOutcome, clearPendingWorkingEvent, publishEvent, room, scheduleAutoHide]
  );

  const dismiss = useCallback(() => {
    if (spec) {
      if ("generated" in spec && spec.generated) {
        const controller = controllers.get(controllerKey(spec.generated));
        if (controller?.snapshot.artifact.sdk === "2") {
          controller.visibility(false);
          cancelAutoHide();
          setBoard((current) => ({ ...current, minimizedByAgent: true }));
          return;
        }
        controller?.dismiss();
      }
      cancelAutoHide();
      cancelHighlightFade();
      clearPendingWorkingEvent();
      finalizeActiveBoard("dismissed");
      void publishEvent({
        type: "whiteboard_event",
        exerciseId: spec.id,
        kind: spec.kind,
        event: "closed",
      });
    }
    setBoard((current) => emptyClientState(current.tutorRevision));
  }, [
    cancelAutoHide,
    cancelHighlightFade,
    clearPendingWorkingEvent,
    finalizeActiveBoard,
    spec,
    publishEvent,
  ]);

  const restore = useCallback(() => {
    const active = specRef.current;
    if (active && "generated" in active && active.generated)
      controllers.get(controllerKey(active.generated))?.reopen();
    setBoard((current) =>
      current.spec
        ? {
            ...current,
            minimizedByAgent: false,
          }
        : current
    );
  }, []);

  return {
    spec,
    renderState,
    minimizedByAgent: board.minimizedByAgent,
    tutorRevision: board.tutorRevision,
    boardHistory,
    sendCardEvent,
    observeUserSpeech,
    dismiss,
    restore,
  };
}

interface SpeechAwareTarget {
  action: string;
  block: string;
  target: string;
  targetId: string;
  expected: string;
  matched: boolean;
}

function speechCoversPhrase(spoken: string, phrase: string): boolean {
  const normalizedSpeech = normalizeAnswer(spoken);
  const normalizedPhrase = normalizeAnswer(phrase);
  if (!normalizedSpeech || !normalizedPhrase) return false;
  const speechTokens = normalizedSpeech.split(" ");
  const phraseTokens = normalizedPhrase.split(" ");
  if (speechTokens.length < phraseTokens.length) return false;
  return speechTokens.some(
    (_, start) =>
      speechTokens.slice(start, start + phraseTokens.length).join(" ") === normalizedPhrase
  );
}

function findSpeechAwareTarget(spec: WhiteboardSpec, spoken: string): SpeechAwareTarget | null {
  if (spec.kind !== "composed") return null;

  for (const [index, block] of spec.blocks.entries()) {
    const blockLabel = ("label" in block && block.label) || `${block.type} #${index + 1}`;
    if (block.type === "pronunciation_focus") {
      return {
        action: "pronunciation_attempt",
        block: blockLabel,
        target: block.phrase,
        targetId: `block.${index + 1}.phrase`,
        expected: block.phrase,
        matched: speechCoversPhrase(spoken, block.phrase),
      };
    }
    if (block.type === "phrase_bank_roleplay") {
      const matchedPhrase = block.phrases.find((phrase) => speechCoversPhrase(spoken, phrase.text));
      const expectedPhrase =
        matchedPhrase ?? block.phrases.find((phrase) => phrase.recommended) ?? block.phrases[0];
      if (!expectedPhrase) return null;
      const phraseIndex = block.phrases.indexOf(expectedPhrase);
      return {
        action: "roleplay_speech_attempt",
        block: blockLabel,
        target: expectedPhrase.text,
        targetId: `block.${index + 1}.phrase.${Math.max(phraseIndex, 0)}`,
        expected: block.phrases.map((phrase) => phrase.text).join(" | "),
        matched: Boolean(matchedPhrase),
      };
    }
  }

  return null;
}
